import { useState, useEffect } from 'react'
import config from 'isomorphic-config'
import { GraphResponse, GraphResponseError } from '@/types'
import analytics from '@/shared-utils/analytics'
import getCookieByName from '@/shared-utils/get-cookie-by-name'
import { RecentActivityData } from '@/components/RecentSearchCards/types'
import isAbortSignalTimeoutSupported from '@/shared-utils/is-abortSignal-supported'
import { jsonContentTypeHeader } from '../server/constants'
import useSeti from './useSeti'

type AbandonedSelection = [RecentActivityData, ...RecentActivityData[]]
type RecentActivityQueryResultSignedIn<T> = {
  readonly abandonedSelectionsbyAuthtokenAndCguid: T
}
type RecentActivityQueryResultSignedOut<T> = {
  readonly abandonedSelectionsbyCguid: T
}

type RecentActivityGraphResponse =
  | GraphResponse<RecentActivityQueryResultSignedIn<AbandonedSelection>>
  | GraphResponse<RecentActivityQueryResultSignedOut<AbandonedSelection>>

type ApiMetadata = {
  appName: string
  appVersion: string
}

const { url, timeout } = config.client['pcln-graph']
const requestUrl = `${url}?gqlOp=recentActivity`

function isValidGraphQLResponse(
  payload: unknown
): payload is RecentActivityGraphResponse {
  if (payload && payload instanceof Object) {
    if ('errors' in payload && Array.isArray(payload.errors)) {
      return true
    }
    if (
      'data' in payload &&
      payload.data &&
      typeof payload.data === 'object' &&
      'abandonedSelectionsbyAuthtokenAndCguid' in payload.data &&
      (payload.data.abandonedSelectionsbyAuthtokenAndCguid === null ||
        (Array.isArray(payload.data.abandonedSelectionsbyAuthtokenAndCguid) &&
          payload.data.abandonedSelectionsbyAuthtokenAndCguid.length > 0))
    ) {
      return true
    }
    if (
      'data' in payload &&
      payload.data &&
      typeof payload.data === 'object' &&
      'abandonedSelectionsbyCguid' in payload.data &&
      (payload.data.abandonedSelectionsbyCguid === null ||
        (Array.isArray(payload.data.abandonedSelectionsbyCguid) &&
          payload.data.abandonedSelectionsbyCguid.length > 0))
    ) {
      return true
    }
  }
  return false
}

function hasGraphErrors(
  payload: RecentActivityGraphResponse
): payload is GraphResponseError {
  if ('errors' in payload && Array.isArray(payload.errors)) {
    return true
  }
  return false
}

/* eslint-disable no-underscore-dangle */
export default function useRecentActivity(
  cguid: string,
  apiMetadata: ApiMetadata,
  signedIn: boolean
) {
  const [recentActivity, setRecentActivity] =
    useState<RecentActivityData | null>(null)
  const HP_HOTEL_ED_SELECTIONS = useSeti('HP_HOTEL_ED_SELECTIONS')
  useEffect(() => {
    const baseLogData = {
      cguid,
      appName: apiMetadata.appName,
      appVersion: apiMetadata.appVersion
    }
    async function fetchRecentActivity() {
      const authToken = getCookieByName('dmc') || ''
      const options = signedIn
        ? {
            method: 'POST',
            signal: isAbortSignalTimeoutSupported()
              ? AbortSignal.timeout(timeout)
              : undefined,
            headers: {
              authToken,
              ...jsonContentTypeHeader,
              'apollographql-client-name': apiMetadata.appName,
              'apollographql-client-version': apiMetadata.appVersion
            },
            body: JSON.stringify({
              query: `query HotelSelection($authToken: String!, $cguid: String!, $limit: Int) {
            abandonedSelectionsbyAuthtokenAndCguid(authToken: $authToken, cguid:$cguid, limit: $limit)
              {
                ... on HotelSelection {
                  URL
                  checkInDate
                  checkOutDate
                  numberOfAdults
                  numberOfRooms
                  temp_hotel {
                    ... on TEMP_CDP_RtlHotel {
                      __typename
                      name
                      neighborhood
                      numberOfReviews
                      starRating
                      averageRating
                      imageURL
                    }
                    ... on TEMP_CDP_ExpressDealHotel {
                      __typename
                      description
                      location
                    }
                    ... on TEMP_CDP_PriceBreakers {
                      __typename
                      description
                      location
                    }
                  }
                }
              }
            }`,
              variables: {
                authToken,
                cguid,
                limit: 1
              }
            })
          }
        : {
            method: 'POST',
            signal: isAbortSignalTimeoutSupported()
              ? AbortSignal.timeout(timeout)
              : undefined,
            headers: {
              ...jsonContentTypeHeader,
              'apollographql-client-name': apiMetadata.appName,
              'apollographql-client-version': apiMetadata.appVersion
            },
            body: JSON.stringify({
              query: `query HotelSelection($cguid: String!, $limit: Int) {
            abandonedSelectionsbyCguid(cguid:$cguid, limit: $limit)
              {
                ... on HotelSelection {
                  URL
                  checkInDate
                  checkOutDate
                  numberOfAdults
                  numberOfRooms
                  temp_hotel {
                    ... on TEMP_CDP_RtlHotel {
                      __typename
                      name
                      neighborhood
                      numberOfReviews
                      starRating
                      averageRating
                      imageURL
                    }
                    ... on TEMP_CDP_ExpressDealHotel {
                      __typename
                      description
                      location
                    }
                    ... on TEMP_CDP_PriceBreakers {
                      __typename
                      description
                      location
                    }
                  }
                }
              }
            }`,
              variables: {
                cguid,
                limit: 1
              }
            })
          }
      try {
        const response = await fetch(requestUrl, options)
        const { status, url: urlValue } = response
        if (!response.ok) {
          analytics.logError({
            message: 'The recent activity graph query response status is > 299',
            url: urlValue,
            status,
            ...baseLogData
          })
        } else {
          const payload = await response.json()
          if (isValidGraphQLResponse(payload)) {
            if (hasGraphErrors(payload)) {
              const firstErrorMessage = payload.errors?.[0]?.message
              const errorMessage = firstErrorMessage
                ? `Error message: ${firstErrorMessage}`
                : 'Unknown error'
              const errorObjectAsString = JSON.stringify(payload.errors)
              const message = `The recent activity graph query response has returned errors. ${errorMessage}`
              analytics.logError({
                message,
                url: requestUrl,
                ...baseLogData,
                errorObjectAsString
              })
            } else if (
              'abandonedSelectionsbyAuthtokenAndCguid' in payload.data
            ) {
              const filteredAbandonedSelection =
                payload.data.abandonedSelectionsbyAuthtokenAndCguid.filter(
                  value =>
                    Object.keys(value.temp_hotel).length > 0 &&
                    (HP_HOTEL_ED_SELECTIONS === 'VARIANT'
                      ? true
                      : value.temp_hotel.__typename === 'TEMP_CDP_RtlHotel')
                )
              if (filteredAbandonedSelection.length > 0) {
                setRecentActivity(filteredAbandonedSelection[0])
              }
            } else {
              const filteredAbandonedSelection =
                payload.data.abandonedSelectionsbyCguid.filter(
                  value =>
                    Object.keys(value.temp_hotel).length > 0 &&
                    (HP_HOTEL_ED_SELECTIONS === 'VARIANT'
                      ? true
                      : value.temp_hotel.__typename === 'TEMP_CDP_RtlHotel')
                )
              if (filteredAbandonedSelection.length > 0) {
                setRecentActivity(filteredAbandonedSelection[0])
              }
            }
          } else {
            analytics.logError({
              message: `The recent activity graph query response for cguid ${cguid} is not as per contract`,
              payload: JSON.stringify(payload)
            })
          }
        }
      } catch (error) {
        const errorMessage =
          error && error instanceof Error
            ? `Error Message: ${error.message} Error Name: ${error.name}`
            : 'Unknown error'
        analytics.logError({
          message: `Failed to call the recent activity graph query. ${errorMessage}`,
          error: JSON.stringify(error)
        })
      }
    }
    void fetchRecentActivity()
  }, [
    apiMetadata.appName,
    apiMetadata.appVersion,
    cguid,
    signedIn,
    HP_HOTEL_ED_SELECTIONS
  ])
  return recentActivity
}
